import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Network, Plus, Pencil, Trash2, Users, ChevronDown, ChevronRight, Shield, Briefcase, UserCheck, Monitor } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getRoleBadge = (ruolo) => {
  const map = {
    super_admin: { label: 'Super Admin', cls: 'bg-violet-100 text-violet-700' },
    admin: { label: 'Admin', cls: 'bg-sky-100 text-sky-700' },
    sub_agent: { label: 'Sub-agente', cls: 'bg-emerald-100 text-emerald-700' },
    back_office: { label: 'Back Office', cls: 'bg-amber-100 text-amber-700' },
    interno: { label: 'Interno', cls: 'bg-slate-100 text-slate-700' },
  };
  const info = map[ruolo] || { label: ruolo, cls: 'bg-slate-100 text-slate-700' };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${info.cls}`}>{info.label}</span>;
};

const getRoleIcon = (ruolo) => {
  const map = {
    super_admin: Shield,
    admin: Shield,
    sub_agent: Briefcase,
    back_office: Monitor,
    interno: UserCheck,
  };
  const Icon = map[ruolo] || Users;
  return <Icon className="w-4 h-4 text-slate-400" />;
};

const RetiVendita = () => {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole('super_admin');
  const [reti, setReti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRete, setExpandedRete] = useState(null);
  const [members, setMembers] = useState({});
  const [loadingMembers, setLoadingMembers] = useState({});

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRete, setEditingRete] = useState(null);
  const [formData, setFormData] = useState({ nome: '', descrizione: '' });

  const fetchReti = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reti-vendita`);
      setReti(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento delle reti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReti(); }, [fetchReti]);

  const fetchMembers = async (reteId) => {
    if (members[reteId]) return;
    setLoadingMembers(prev => ({ ...prev, [reteId]: true }));
    try {
      const res = await axios.get(`${API_URL}/api/reti-vendita/${reteId}/members`);
      setMembers(prev => ({ ...prev, [reteId]: res.data }));
    } catch (e) {
      toast.error('Errore nel caricamento dei membri');
    } finally {
      setLoadingMembers(prev => ({ ...prev, [reteId]: false }));
    }
  };

  const toggleExpand = (reteId) => {
    if (expandedRete === reteId) {
      setExpandedRete(null);
    } else {
      setExpandedRete(reteId);
      fetchMembers(reteId);
    }
  };

  const openCreate = () => {
    setEditingRete(null);
    setFormData({ nome: '', descrizione: '' });
    setDialogOpen(true);
  };

  const openEdit = (rete) => {
    setEditingRete(rete);
    setFormData({ nome: rete.nome, descrizione: rete.descrizione || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.nome.trim()) {
      toast.error('Il nome è obbligatorio');
      return;
    }
    try {
      if (editingRete) {
        await axios.put(`${API_URL}/api/reti-vendita/${editingRete.id}`, formData);
        toast.success('Rete aggiornata');
      } else {
        await axios.post(`${API_URL}/api/reti-vendita`, formData);
        toast.success('Rete creata');
      }
      setDialogOpen(false);
      fetchReti();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async (reteId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa rete? Gli utenti assegnati perderanno l\'assegnazione.')) return;
    try {
      await axios.delete(`${API_URL}/api/reti-vendita/${reteId}`);
      toast.success('Rete eliminata');
      setReti(prev => prev.filter(r => r.id !== reteId));
      if (expandedRete === reteId) setExpandedRete(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reti-vendita-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Reti di Vendita</h1>
          <p className="text-slate-500 mt-1">Gestisci le reti di vendita e i loro componenti</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={openCreate} data-testid="create-rete-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuova Rete
          </Button>
        )}
      </div>

      {reti.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Network className="w-12 h-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Nessuna rete di vendita</h3>
            <p className="text-slate-500 mt-1">Crea la prima rete per organizzare i tuoi team</p>
            {isSuperAdmin && (
              <Button className="mt-4" onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" /> Crea Rete
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reti.map((rete) => (
            <Card key={rete.id} className="overflow-hidden" data-testid={`rete-card-${rete.id}`}>
              <div
                className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleExpand(rete.id)}
                data-testid={`rete-toggle-${rete.id}`}
              >
                <div className="flex items-center gap-4">
                  {expandedRete === rete.id ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-sky-100">
                    <Network className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{rete.nome}</h3>
                    {rete.descrizione && (
                      <p className="text-sm text-slate-500 mt-0.5">{rete.descrizione}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    {rete.num_membri} {rete.num_membri === 1 ? 'membro' : 'membri'}
                  </Badge>
                  {!rete.attiva && (
                    <Badge variant="destructive">Disattivata</Badge>
                  )}
                  {isSuperAdmin && (
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(rete)} data-testid={`edit-rete-${rete.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDelete(rete.id)} data-testid={`delete-rete-${rete.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Members section */}
              {expandedRete === rete.id && (
                <div className="border-t bg-slate-50/50 px-6 py-4">
                  {loadingMembers[rete.id] ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600" />
                    </div>
                  ) : !members[rete.id] || members[rete.id].length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">Nessun membro assegnato a questa rete</p>
                  ) : (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-slate-600 mb-3">Componenti della rete ({members[rete.id].length})</h4>
                      <div className="grid gap-2">
                        {members[rete.id].map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-slate-200"
                            data-testid={`member-${member.id}`}
                          >
                            <div className="flex items-center gap-3">
                              {getRoleIcon(member.ruolo)}
                              <div>
                                <p className="text-sm font-medium text-slate-900">{member.nome} {member.cognome}</p>
                                <p className="text-xs text-slate-500">{member.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getRoleBadge(member.ruolo)}
                              <span className={`w-2 h-2 rounded-full ${member.attivo ? 'bg-emerald-500' : 'bg-red-400'}`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="rete-dialog">
          <DialogHeader>
            <DialogTitle>{editingRete ? 'Modifica Rete di Vendita' : 'Nuova Rete di Vendita'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Es. Rete Sicilia"
                data-testid="rete-nome-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input
                value={formData.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                placeholder="Descrizione opzionale"
                data-testid="rete-descrizione-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} data-testid="save-rete-btn">
              {editingRete ? 'Salva Modifiche' : 'Crea Rete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RetiVendita;
